import _ from 'lodash';
import { getFormValues, destroy } from 'redux-form';
import { replace } from 'react-router-redux';
import moment from 'moment';
import qs from 'qs';

import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import { loading, success, error } from 'State/actions';
import * as CommonStateSelectors from 'State/selectors';
import * as POSSelectors from 'PointOfSale/selectors';
import * as MealCounterActions from 'PointOfSale/modules/cepCard/state/actions';
import * as TeacherActions from 'PointOfSale/modules/teacher/state/actions';
import getDatabase from 'Common/DB';
import {
  NOTIFICATION_DIALOG_MESSAGES,
  DATE_FORMAT_FOR_SERVER,
} from 'Common/Constants';
import * as MealTypes from 'PointOfSale/modules/mealTypes/state/types';
import {
  formatServedItems,
  extractServedItemOrder,
  getItemWithData,
} from 'PointOfSale/modules/common/helpers';
import {
  isValidTransaction,
  buildTransactionRequest,
} from 'PointOfSale/helpers/transactions';
import {
  AUTO_SERVE_OPTIONS,
  SessionTypes,
  POS_ROUTES,
  QueueStatusEnum,
} from 'PointOfSale/modules/common/constants';
import * as ServingOptionsAction from 'PointOfSale/modules/servingOptions/state/actions';
import * as QueueActions from 'PointOfSale/modules/queue/state/actions';
import * as IndividualServingActions from 'PointOfSale/modules/individualServing/state/actions';
import { UPDATE_SERVING_SITE_MEAL_COUNT } from 'PointOfSale/modules/setDateAndSite/state/types';
import { TransactionEnum } from 'Common/FieldEnum';
import { getOfflineTransactionType } from 'Offline/helpers';
import { GET_STUDENTS_PAGE_SIZE } from 'PointOfSale/helpers/Constants';

import * as types from './types';
import ValidateTransaction from './transactionValidator';

const ALLERGEN_STRING_DELIMTER = ', ';

export const getStudents = async ({ siteId, search, MPWWebAPI }) => {
  if (!MPWWebAPI) {
    return [];
  }

  let baseURL = `Serving/GetStudents`;
  let queryParams = { PageSize: GET_STUDENTS_PAGE_SIZE, PageNumber: 1 };

  if (search) {
    queryParams.Search = search;
  }

  const queryString = qs.stringify(queryParams);

  if (siteId) {
    baseURL = `Serving/GetStudents/${siteId}`;
  }

  const response = await MPWWebAPI.getGeneric(`${baseURL}?${queryString}`);

  let students = response.data.Data;
  const meta = response.data.Meta;

  if (meta.HasNextPage) {
    const getStudentRequests = [];

    for (let pageNumber = 2; pageNumber <= meta.Pages; pageNumber++) {
      queryParams = {
        PageSize: GET_STUDENTS_PAGE_SIZE,
        PageNumber: pageNumber,
      };

      if (search) {
        queryParams.Search = search;
      }

      const queryString = qs.stringify(queryParams);

      getStudentRequests.push(
        MPWWebAPI.getGeneric(`${baseURL}?${queryString}`)
      );
    }

    const responses = await Promise.all(getStudentRequests);

    responses.forEach((res) => {
      students = students.concat(res.data.Data);
    });
  }

  return students;
};

/** Retrieves data required for serving */
export const getPosData = (siteId, date) => async (
  dispatch,
  getState,
  MPWWebAPI
) => {
  const state = getState();
  dispatch(loading(types.GET_POS_DATA_FETCHING, true));
  const isOnline =
    CommonStateSelectors.getOnline(state) &&
    CommonStateSelectors.getServerUp(state);
  try {
    if (isOnline) {
      const formattedDate = moment(date).format(DATE_FORMAT_FOR_SERVER);
      const [
        GetNutritionCodesResponse,
        GetServedItemsResponse,
        GetItemCategoriesResponse,
        GetSiteMasterResponse,
        GetAllergenResponse,
        GetTaxRatesByStatusResponse,
      ] = await Promise.all([
        MPWWebAPI.getGeneric(`Serving/GetNutritionCodes`),
        MPWWebAPI.getGeneric(
          `Serving/GetServedItems/${siteId}/${formattedDate}`
        ),
        MPWWebAPI.getGeneric('Serving/GetItemCategories'),
        MPWWebAPI.getGeneric('Sitemaster'),
        MPWWebAPI.getGeneric('Allergen'),
        MPWWebAPI.getGeneric(`TaxRate/GetTaxRatesBySiteId/${siteId}`),
      ]);

      const students = await getStudents({ siteId, MPWWebAPI });

      const sites = GetSiteMasterResponse.data.Data;

      const allergens = GetAllergenResponse.data.Data;
      const taxRatesByStatus = GetTaxRatesByStatusResponse.data.Data;
      const itemCategories = GetItemCategoriesResponse.data.Data;
      const nutritionCodes = GetNutritionCodesResponse.data.Data;

      students.forEach((s) => {
        const trimmed = _.trim(s.AllergenString);
        s.Allergens = trimmed ? _.split(trimmed, ALLERGEN_STRING_DELIMTER) : [];
      });
      const servedItems = GetServedItemsResponse.data.Data;
      const servedItemsOrder = extractServedItemOrder(servedItems);
      const payload = {
        siteId,
        students,
        nutritionCodes,
        servedItems,
        sites,
        allergens,
        itemCategories,
        servedItemsOrder,
        taxRatesByStatus,
      };

      const foodItems = formatServedItems(servedItems);

      await dispatch(success(types.GET_POS_DATA_FETCH, payload));
      await dispatch(success(MealTypes.GET_SERVED_ITEMS_FETCH, foodItems));
      dispatch(success(types.POS_SET_SITES, sites));
    } else {
      const databaseName = CommonStateSelectors.getDatabaseName(state);
      const DB = await getDatabase(databaseName);
      const servedItems = await DB.ServedItems.where({
        SiteId: siteId,
      }).toArray();

      const sites = await DB.Sites.toArray();
      const foodItems = formatServedItems(servedItems);
      const itemCategories = await DB.ItemCategories.toArray();
      const nutritionCodes = await DB.NutritionCodes.toArray();
      const taxRatesByStatus = await DB.TaxRatesByStatus.toArray();

      const servedItemsOrder = extractServedItemOrder(servedItems);
      const payload = {
        servedItems,
        itemCategories,
        nutritionCodes,
        servedItemsOrder,
        taxRatesByStatus,
        offline: true,
      };

      await dispatch(success(types.GET_POS_DATA_FETCH, payload));
      await dispatch(success(MealTypes.GET_SERVED_ITEMS_FETCH, foodItems));
      dispatch(success(types.POS_SET_SITES, sites));
    }
  } catch (err) {
    dispatch(error(types.GET_POS_DATA_ERROR, err));
    dispatch(
      error(MealTypes.GET_SERVED_ITEMS_FETCH, {
        Breakfast: {},
        Lunch: {},
        Supper: {},
        Other: {},
      })
    );
  }
  dispatch(loading(types.GET_POS_DATA_FETCHING, false));
};

/**
 * @param {Transaction} transaction
 * @param {Object} data
 * @param {Object} options
 * @param {Boolean} options.displayErrorMessages - determines whether to dispatch the error, the error will be shown by the dialog
 *
 * @return {Promise<Array[Boolean, Array[Errors]]>} [successful, Errors]
 */
export const processTransaction = (
  transaction,
  data,
  options = { displayErrorMessages: true }
) => async (dispatch, getState, MPWWebAPI) => {
  // Add extra properties required for the transaction to process.
  transaction = buildTransactionRequest(transaction);

  const { transaction: validatedTransaction, errors } = await dispatch(
    ValidateTransaction(transaction, data, options)
  );

  if (errors.length) {
    return [false, errors];
  }

  // If no errors were found during validation then we can attempt to write the transaction.
  const [success, error] = await dispatch(
    writeTransaction(validatedTransaction)
  );

  if (success) {
    dispatch(postTransactionCleanup(validatedTransaction));
  }

  return [success, error ? [error] : []];
};

/**
 * @param {Transaction} transaction
 * @param {Object} options
 * @param {Boolean} options.ignoreError - determines whether to dispatch the error, the error will be shown by the dialog
 * @return {Promise<Array[Boolean, Error]>} [successful, error] - successful is true if written successfully, false otherwise, if false then the second index will be the error object
 */
export const writeTransaction = (transaction, options = {}) => async (
  dispatch,
  getState,
  MPWWebAPI
) => {
  const state = getState();

  if (!isValidTransaction(transaction)) {
    // Add extra properties required for the transaction to process.
    transaction = buildTransactionRequest(transaction);

    if (!isValidTransaction(transaction)) {
      return [false, NOTIFICATION_DIALOG_MESSAGES.TRANSACTION_GENERIC_ERROR];
    }
  }

  dispatch(writeTransactionLocal(transaction));
  if (
    CommonStateSelectors.getOnline(state) &&
    CommonStateSelectors.getServerUp(state)
  ) {
    dispatch(loading(types.WRITE_TRANSACTION_FETCHING, true));
    try {
      const response = await MPWWebAPI.postGenericInsertUpdate(
        'Serving/Transaction',
        transaction
      );
      /** Considered successful if the response data is an empty string, otherwise Data is an error message */
      if (response.data.Data === '') {
        /** Remove locally because it was successful */
        dispatch(deleteTransactionLocal(transaction));
        dispatch(success(types.WRITE_TRANSACTION_FETCH, transaction));
        dispatch(destroy(TRANSACTION_FORM_NAME));
        dispatch(loading(types.WRITE_TRANSACTION_FETCHING, false));

        if (_.get(response.data, 'Extra.MealCount')) {
          // Update meal count.
          dispatch({
            type: UPDATE_SERVING_SITE_MEAL_COUNT,
            payload: response.data.Extra,
          });
        }

        return [true, null];
      } else {
        const err = {
          Title: NOTIFICATION_DIALOG_MESSAGES.TRANSACTION_GENERIC_ERROR.title,
          Message: response.data.Data,
        };
        throw err;
      }
    } catch (err) {
      let errorObj = err;
      if (_.has(err, 'response.data.Errors[0]')) {
        const errorFromResponse = err.response.data.Errors[0];
        if (errorFromResponse) {
          errorObj = {
            Title: errorFromResponse.Title,
            Message: errorFromResponse.Message,
          };
        } else {
          errorObj = {
            Title: NOTIFICATION_DIALOG_MESSAGES.TRANSACTION_GENERIC_ERROR.title,
            Message:
              NOTIFICATION_DIALOG_MESSAGES.TRANSACTION_GENERIC_ERROR.message,
          };
        }
      }
      dispatch(deleteTransactionLocal(transaction));
      if (!options.ignoreError) {
        dispatch(error(types.WRITE_TRANSACTION_FETCH_ERROR, errorObj));
      }
      dispatch(loading(types.WRITE_TRANSACTION_FETCHING, false));
      return [false, errorObj];
    }
  } else {
    dispatch(success(types.RESET_CURRENT_TRANSACTION, transaction));
    dispatch(destroy(TRANSACTION_FORM_NAME));
  }

  // Offline transaction was written
  return [true, null];
};

export const postTransactionCleanup = (transaction) => (
  dispatch,
  getState,
  MPWWebAPI
) => {
  const state = getState();
  const activeSession = POSSelectors.getActiveSession(state);
  const hasMealCounterSession = POSSelectors.hasMealCounterSession(state);
  const hasTeacherSession = POSSelectors.hasTeacherSession(state);

  /** Fetch updates if there were multiple students */
  dispatch(
    fetchStudent(transaction.StudentIdSeed, {
      fetch: true,
    })
  );
  dispatch(IndividualServingActions.setLastTransactionProfile());

  switch (activeSession) {
    case SessionTypes.Queue:
      if (hasMealCounterSession) {
        dispatch(MealCounterActions.removeStudent());
      } else {
        dispatch(QueueActions.removeFromQueue());
      }
      if (hasTeacherSession) {
        dispatch(
          TeacherActions.resetStudentSelected(transaction.StudentIdSeed)
        );
      }
      break;

    case SessionTypes.Teacher:
      dispatch(TeacherActions.removeStudent(transaction.StudentIdSeed));
      dispatch(replace(POS_ROUTES.TEACHER));

      break;
    default:
  }
};

export const writeTransactionLocal = (transaction) => ({
  type: types.WRITE_TRANSACTION_LOCAL,
  payload: {
    ...transaction,
    [TransactionEnum.TransactionType]: getOfflineTransactionType(
      transaction[TransactionEnum.TransactionType]
    ),
  },
});

export const deleteTransactionLocal = (transaction) => ({
  type: types.DELETE_TRANSACTION_LOCAL,
  payload: transaction,
});

/**
 * Generic error for when writing to local db
 * Errors are stored in PointOfSaleState.common.offlineErrors
 */
export const writeLocalError = (type, error) => ({
  type: types.WRITE_LOCAL_ERROR,
  payload: {
    type,
    error,
  },
});

/**
 * Resets PointOfSaleState.common.offlineErrors[key] to null
 * @param {String} type
 */
export const resetWriteLocalError = (type) => ({
  type: types.RESET_WRITE_LOCAL_ERROR,
  payload: {
    type,
  },
});

/**
 * Retrieves transaction from current transaction form state
 * does not return with TransactionType
 */
export const updateCurrentTransaction = () => async (dispatch, getState) => {
  /** Pull data from state */
  const state = getState();
  const databaseName = CommonStateSelectors.getDatabaseName(state);
  const DB = await getDatabase(databaseName);

  const {
    PrimarySiteId,
    MealType,
    WorkingDT,
    ServerId,
  } = POSSelectors.getServingDetails(state);

  const profile = POSSelectors.getProfile(state);
  /** get transaction data */
  const StudentIdSeed = _.get(profile, 'IdSeed');

  const transactionFormValues = getFormValues(TRANSACTION_FORM_NAME)(state);

  if (StudentIdSeed && transactionFormValues) {
    const AmountPaid = transactionFormValues.AmountPaid || 0;
    const BoughtItemsWithData = [];
    const BoughtItems = transactionFormValues.BoughtItems || [];

    for (const { ItemId, Qty = 1, UnitPrice } of BoughtItems) {
      const item = await DB.ServedItems.where({
        ItemId,
        SiteId: PrimarySiteId,
      }).first();

      // If a unit price is set it will override the normal price.
      if (UnitPrice) {
        item.UnitPrice = UnitPrice;
      }

      const itemWithData = getItemWithData(item, profile, Qty, MealType);
      BoughtItemsWithData.push(itemWithData);
    }

    const CurrentTransaction = {
      ...transactionFormValues,
      AmountPaid,
      PrimarySiteId,
      MealType,
      StudentIdSeed,
      BoughtItems: BoughtItemsWithData,
      WorkingDT,
      ServerId,
    };
    dispatch(success(types.UPDATE_CURRENT_TRANSACTION, CurrentTransaction));
  } else {
    dispatch(success(types.UPDATE_CURRENT_TRANSACTION, {}));
  }
};

export const getTransactionHistory = (idSeed, date) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.GET_STUDENT_TRANSACTION_HISTORY_FETCHING, true));
    try {
      const response = await MPWWebApi.getGeneric(
        `Student/${idSeed}/TransactionHistory/${date}`
      );
      dispatch(
        success(types.GET_STUDENT_TRANSACTION_HISTORY, response.data.Data)
      );
    } catch (err) {
      dispatch(
        error(types.GET_STUDENT_TRANSACTION_HISTORY_ERROR, err.response)
      );
    } finally {
      dispatch(loading(types.GET_STUDENT_TRANSACTION_HISTORY_FETCHING, false));
    }
  };
};

export const setViewCurrentStudentHistory = (value) => ({
  type: types.SET_VIEW_CURRENT_STUDENT_HISTORY,
  payload: value,
});

/**
 * Action that retrieves the student's data and updates all instances of the student in POS
 * Update currentProfile.profile, student loaded in teacher, student loaded in queue if the idSeed matches
 * @param {Number} idSeed
 * @param {Object} options
 * @param {Boolean} options.fetch  - override to not check whether it should update and just do the update
 * @param {Boolean} options.picture - override to retrieve picture or not
 * @return {Promise<Array[Object, Error]>}
 */
export const fetchStudent = (idSeed, options = {}) => async (
  dispatch,
  getState,
  MPWWebAPI
) => {
  const state = getState();

  const shouldFetch = !_.isNil(options.fetch)
    ? options.fetch
    : POSSelectors.shouldUpdateStudent(state, idSeed);

  const isOnline =
    CommonStateSelectors.getOnline(state) &&
    CommonStateSelectors.getServerUp(state);

  const {
    PrimarySiteId,
    WorkingDate,
    MealType,
  } = POSSelectors.getServingDetails(state);

  /** Defaults to not fetch the student's picture, we already have the student's picture from previous request */
  const ReturnPicture = !_.isNil(options.picture) ? options.picture : false;

  if (isOnline && shouldFetch) {
    try {
      const model = {
        IdSeed: idSeed,
        PrimarySiteId,
        WorkingDate,
        MealType,
        ReturnPicture,
      };
      const studentResponse = await MPWWebAPI.postGenericInsertUpdate(
        '/Serving/GetStudentDetail',
        model
      );
      const student = studentResponse.data.Data;
      dispatch(updateStudent(idSeed, student));
      return [student, null];
    } catch (err) {
      dispatch(error(types.FETCH_STUDENT_ERROR, err));
      return [null, err];
    }
  }
  return [{}, null];
};

export const updateStudent = (idSeed, update) => {
  /**
   * If Picture is null, remove it from the update object
   * to prevent overwrite of student's Picture
   */
  const student = _.isNil(update.Picture) ? _.omit(update, 'Picture') : update;

  return {
    type: types.UPDATE_STUDENT,
    payload: {
      idSeed,
      update: student,
    },
  };
};

export const switchSession = ({ key, data }) => (dispatch, getState) => {
  const state = getState();

  const queueStatus = POSSelectors.getQueueStatus(state);

  if (queueStatus === QueueStatusEnum.ON) {
    dispatch(QueueActions.changeQueueStatus(QueueStatusEnum.OFF));
  }

  const session = key;
  const sessionData = data;
  dispatch(ServingOptionsAction.setActiveSession(session));

  switch (session) {
    case SessionTypes.Queue:
      if (sessionData) {
        if (sessionData.queueType === AUTO_SERVE_OPTIONS.MealCounter) {
          dispatch(replace(POS_ROUTES.MEAL_COUNTER));
        } else {
          if (sessionData.autoServe) {
            dispatch(replace(POS_ROUTES.QUEUE));
          } else {
            dispatch(replace(POS_ROUTES.QUEUE_NEW));
          }
        }
        dispatch(QueueActions.shuffleQueue(0));
      }
      break;
    case SessionTypes.Teacher:
      dispatch(replace(POS_ROUTES.TEACHER));
      break;
    default:
  }
};

export const setKeypadReadingLeft = (reading) => ({
  type: types.SET_KEYPAD_READING_LEFT,
  payload: reading,
});

export const setKeypadReadingRight = (reading) => ({
  type: types.SET_KEYPAD_READING_RIGHT,
  payload: reading,
});

export const moveItem = (itemId, startPosition, endPosition) => ({
  type: types.MOVE_ITEM,
  payload: {
    itemId,
    startPosition,
    endPosition,
  },
});



// WEBPACK FOOTER //
// ./src/pages/pointofsale/state/actions.js